import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, HostListener } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { concat, Subscription, of, from, Observable } from 'rxjs';
import { map, concatMap, switchMap, finalize } from 'rxjs/operators';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Title } from "@angular/platform-browser";

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { DocumentStackService } from 'src/app/services/document-stack.service';
import { SecurityGroups } from 'src/app/shared/securitygroups';
import { Document, DocItems, DocumentExport, ImportItemFields } from 'src/app/shared/document';
import { DocTypes } from 'src/app/shared/doc-list-entry';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { formatNumber } from '@angular/common';

@Component({
  selector: 'app-edit-document-inventory',
  templateUrl: './edit-document-inventory.component.html',
  styleUrls: ['./edit-document-inventory.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Document editor for DOCTYPE 1 (inventory / stock-check).
 *
 * All party-label getters (`side1Label`, `side2Label`, `company1Label`, etc.) return fixed
 * Ukrainian/Russian strings — there is no `_docType` switching because this editor handles
 * only one document type.
 *
 * Loading follows the standard `concatMap` chain: `getDocumentById` → `getDocumentNameById`
 * → `getDocTypeById`, with a `finalize` snapshot to `documentCopy` for change-detection.
 * New-document mode reads from `DocumentStackService` at index 0.
 *
 * `onConfirm` uses `concat()` (not `concatMap`) so that after a PUT it re-fetches
 * `getDocumentNameById` to refresh the browser title — an extra step not present in the
 * receipt/spec/po siblings.
 *
 * Subscription management is manual (old-style named fields). `noChangesThere` skips
 * computed summary properties (`DOCVSUM`, `DOCSUM`, etc.) when comparing header fields.
 *
 * `onDocumentCompanyChanges` carries real business logic: it copies `DOCMANDEPID` into
 * `DOCCLIDEPID` and `DOCMANPERID` into `DOCPERSONID` from the incoming event.
 *
 * Two section-visibility helpers are provided (`showConditions`, `showContent`); there is
 * no `showTransactions` variant for this document type.
 */
export class EditDocumentInventoryComponent implements OnInit, OnDestroy, OnChanges, ComponentCanDeactivate {

  globalSettinsData: GlobalSettingsData;

  /*private*/ _docId: number = 0;
  /*private*/ _docType: number = 1;

  DOCDATE: Date;
  DOCEXPDATE: Date;
  DOCVALDATE: Date;

  private _initialQuery: Subscription;
  private _productInfoQuery: Subscription;

  private _deleteDocQuery: Subscription;
  private _childDocQuery: Subscription;

  file: any[];

  document: Document;
  documentCopy: Document;

  docTypeInfo: DocTypes;

  displayDC: boolean;

  displayND: boolean;

  displayMD: boolean;

  confirmDeleteDocumentOn: boolean = false;

  confirmDeleteDocumentText: string;
  confirmExportDocumentText: string;

  documentSecurityGroup: SecurityGroups;

  showTabRowInfo: boolean = false;
  showTabDetails: boolean = true;
  showTabSeller: boolean = true;
  showTabCustomer: boolean = false;
  showTabConditions: boolean = true;
  showTabTable: boolean = true;

  result: any[];

  private _documentQuery: Subscription;
  private _docNameQuery: Subscription;
  private _documentSaveQuery: Subscription;
  private _documentPostQuery: Subscription;

  docHeaderStatement: string = '';
  docFooterStatement: string = '';

  /** Primary organisation panel heading. */
  public get side1Label(): string { return 'Основная организация'; };
  /** Secondary data panel heading. */
  public get side2Label(): string { return 'Вспомогательные данные (скрыть)'; };

  /** Main company field label. */
  public get company1Label(): string { return 'Основная организация:'; };
  /** Main contact person field label. */
  public get person1Label(): string { return 'Контактное лицо:'; };
  /** Primary warehouse section heading. */
  public get department1Label(): string { return 'Основной склад'; };
  /** Primary legal entity label. */
  public get lcoLabel(): string { return 'Юридическое лицо:'; };
  /** Primary responsible representative label. */
  public get lcoPer1Label(): string { return 'Ответственный представитель:'; };
  /** Primary warehouse field label. */
  public get lcoDep1Label(): string { return 'Склад:'; };

  /** Secondary company field label. */
  public get company2Label(): string { return 'Организация:'; };
  /** Secondary contact person field label. */
  public get person2Label(): string { return 'Контактное лицо:'; };
  /** Secondary warehouse field label. */
  public get department2Label(): string { return 'Основной склад:'; };
  /** Secondary legal entity label. */
  public get pcoLabel(): string { return 'Юридическое лицо:'; };
  /** Secondary responsible representative label. */
  public get pcoPer2Label(): string { return 'Ответственный представитель:'; };
  /** Secondary warehouse field label. */
  public get pcoDep2Label(): string { return 'Склад:'; };

  /** General conditions field label. */
  public get conditions1Label(): string { return 'Общие условия проведения:'; };
  /** Execution deadline field label. */
  public get conditions2Label(): string { return 'Срок выполнения:'; };
  /** First note field label. */
  public get conditions3Label(): string { return 'Примечание:'; };
  /** Second note field label. */
  public get conditions4Label(): string { return 'Примечание:'; };

  documentSpecific: DocumentExport;

  toolbarOptions: any = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],

    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean']                                         // remove formatting button
  ];

  modules: any = {
    toolbar: this.toolbarOptions
  };

  /** `true` when no header field has changed since the last save; computed totals are excluded. */
  public get noChangesThere(): boolean {
    let equal: boolean = true;
    if (this.document && this.documentCopy) {
      for (var props in this.document) {
        //console.log('*** EditDocumentInventoryComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])

        // Filter out document summury properties (by fact they do not modify document content)

        switch (props) {
          case 'items':
          case 'DOCITEMS':
          case 'DOCTOTALITEMS':
          case 'DOCSUMU4ETKA':
          case 'DOCVSUM':
          case 'DOCSUMWOVAT':
          case 'DOCVAT':
          case 'DOCSUM':
          case 'DOCNAMESTR':
          case 'DOCSUMPROFIT':
            break;

          default:
            equal = (this.document[props] == this.documentCopy[props]);
            break;
        }
        if (!equal) {
          //console.log('*** EditDocumentInventoryComponent in noChangesThere: not equal: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])
          break;
        }
      }
      //console.log('*** EditDocumentInventoryComponent in noChangesThere: ' + equal)
    }
    return equal;
  }

  /** `true` when the document object is not yet loaded. */
  public get formInvalid(): boolean {
    return (
      !this.document
    );
  }

  /** `true` when the document is loaded, saved, and has an existing ID — enables child-doc creation. */
  public get canCreate(): boolean { return (this._docId) && (this.document) && (this.noChangesThere); }

  /** `true` when the document is loaded and has an existing ID — enables delete. */
  public get canDelete(): boolean { return (this._docId) && (this.document && true); }

  exportDS: boolean;

  exportConfirmDlg: boolean = false;

  displayImportDlg: boolean = false;

  knockKnock: boolean = false;

  importParamsMap: ImportItemFields[] = [
    { fieldName: 'PRODCODE', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
    { fieldName: 'DOCITEMQTY', fieldLabel: 'Количество', fieldIsMandatory: true, fieldType: 'num' },
    { fieldName: 'DOCITEMPRICE', fieldLabel: 'Цена товара', fieldIsMandatory: true, fieldType: 'num' },
    //{ fieldName: 'U4ETKA', fieldLabel: 'Учетка', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMPRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'DOCITEMVPRICE', fieldLabel: 'Цена в валюте', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMTEXT1', fieldLabel: 'Примечание 1', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'DOCITEMTEXT2', fieldLabel: 'Примечание 2', fieldIsMandatory: false, fieldType: 'str' },
  ];

  editing: boolean = false;

  public get docTypeInvoice(): boolean { return (this._docId) && (this.document) && ([1, 12].includes(this.document.DOCTYPE)); }
  public get docTypePayment(): boolean { return (this._docId) && (this.document) && ([8, 9, 11].includes(this.document.DOCTYPE)); }

  public get thereIsManufacture(): boolean {
    return (this._docId) &&
      (this.document) &&
      ([1].includes(this.document.DOCTYPE)) &&
      (((this.document.items || []).find(e => e.PRODKIND == 1)) != null)
      ;
  }

  constructor(
    private docApi: DocumentApiService,
    //private restapi: RestApiService,
    //private prodApi: ProductApiService,
    //private cliApi: ClientApiService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private globalsettingsservice: GetglobalsettingsService,
    private documentStack: DocumentStackService,
    private titleService: Title
  ) {
  }

  /** Guards navigation; blocks when there are unsaved changes or an active inline edit. */
  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return (this.noChangesThere && !this.editing);
  }

  /** Unsubscribes all active queries. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentInventoryComponent in ngOnDestroy')

    if (this._initialQuery) { this._initialQuery.unsubscribe(); }
    if (this._documentQuery) { this._documentQuery.unsubscribe(); }
    if (this._documentSaveQuery) { this._documentSaveQuery.unsubscribe(); }
    if (this._documentPostQuery) { this._documentPostQuery.unsubscribe(); }

    if (this._docNameQuery) { this._docNameQuery.unsubscribe(); }

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
    if (this._deleteDocQuery) { this._deleteDocQuery.unsubscribe(); }
    if (this._childDocQuery) { this._childDocQuery.unsubscribe(); }
  }

  /** Logs Angular change-detection cycles (diagnostic only). */
  ngOnChanges(changes: SimpleChanges): void {
    console.log('*** EditDocumentInventoryComponent ngOnChanges: ===> ' + JSON.stringify(changes));
  }

  /**
   * Loads the document via a `concatMap` chain: `getDocumentById` → `getDocumentNameById`
   * → `getDocTypeById`. `finalize` snapshots `documentCopy` for change-detection.
   * When `_docId` is 0 (new-document mode), reads from `DocumentStackService` at index 0.
   */
  loadDocumentContent() {
    console.log('*** EditDocumentInventoryComponent loadDocumentContent: ===> Started');
    if (this._docId) {
      console.log('*** EditDocumentInventoryComponent loadDocumentContent docid: ' + this._docId);

      this._documentQuery =
        this.docApi.getDocumentById(this._docId).pipe(

          concatMap(doc => {

            this.document = doc[0];
            this.DOCDATE = new Date(this.document.DOCDATE);
            this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
            this.DOCVALDATE = new Date(this.document.DOCVALDATE);
            this._docType = this.document.DOCTYPE;

            console.log('*** EditDocumentPoComponent in map loadDocumentContent: ===> ' + this.DOCDATE);

            return of(this.document);
          }),

          concatMap(doc => this.docApi.getDocumentNameById(doc.DOCID).pipe(map(data => {
            let docName: any = data[0];

            console.log('*** EditDocumentPoComponent loadDocumentName: ===> doc 0: ' + JSON.stringify(docName));

            if (docName) {
              this.document.DOCNAMESTR = docName.DOCNAMESTR;
              this.titleService.setTitle(this.document.DOCNAMESTR);
            }
            return doc;
          }))),

          concatMap(doc => this.docApi.getDocTypeById(doc.DOCTYPE).pipe(map(data => {
            console.log('*** EditDocumentPoComponent in get DOCTYPE: ===> ' + JSON.stringify(data));

            this.docTypeInfo = data[0];

            this.document.HeaderStatement = this.docTypeInfo.DOCTEXTBEFORE;
            this.document.FooterStatement = this.docTypeInfo.DOCTEXTAFTER;
          }))),

          finalize(() => { this.documentCopy = { ... this.document }; })

        ).subscribe();

    //  this._documentQuery =
    //    concat(
    //      this.docApi.getDocumentById(this._docId)
    //        .pipe(map(data => {
    //          this.document = data[0];
    //          this.DOCDATE = new Date(this.document.DOCDATE);
    //          this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
    //          this.DOCVALDATE = new Date(this.document.DOCVALDATE);

    //          console.log('*** EditDocumentInventoryComponent in map loadDocumentContent: ===> ' + this.DOCDATE);
    //        })),
    //      this.docApi.getDocumentNameById(this._docId)
    //        .pipe(map((data) => {
    //          let docName: any = data[0];
    //          console.log('*** EditDocumentInventoryComponent loadDocumentName: ===> doc 0: ' + JSON.stringify(docName));

    //          if (docName) {
    //            this.document.DOCNAMESTR = docName.DOCNAMESTR;
    //            this.titleService.setTitle(this.document.DOCNAMESTR);

    //          }
    //        })),
    //      this.docApi.getDocTypeById(1)
    //        .pipe(map(data => {
    //          console.log('*** EditDocumentInventoryComponent in get DOCTYPE: ===> ' + JSON.stringify(data));

    //          let res: DocTypes = data[0];

    //          this.document.HeaderStatement = res.DOCTEXTBEFORE;
    //          this.document.FooterStatement = res.DOCTEXTAFTER;

    //        })),
    //      of('Finally')
    //        .pipe(map((data) => {
    //          console.log(data);
    //          this.documentCopy = { ... this.document };
    //          //  environment.production || console.table(this.document);
    //          //  environment.production || console.table(this.document.items);
    //        })),
    //    )
    //      .subscribe(() => {
    //        console.log('*** EditDocumentInventoryComponent loadDocumentContent: ===> document loaded');
    //        //environment.production || console.table(this.document);

    //      });
    } else {
      /*
       * (this._prodId == 0) New Document Mode
       *
       * get Document from Document Stack
       */
      let newIndex = 0;

      /*
       * Request new Index from query string param.
       */
      this.document = this.documentStack.getDocument(newIndex);
      if (this.document) {

        this.DOCDATE = new Date(this.document.DOCDATE);
        this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
        this.DOCVALDATE = new Date(this.document.DOCVALDATE);
      }
    }
    console.log('*** EditDocumentInventoryComponent loadDocumentContent: ===> Ended');
  }

  /** Reads global settings and subscribes to route params to trigger `loadDocumentContent`. */
  ngOnInit(): void {
    console.log('*** EditDocumentInventoryComponent in ngOnInit')
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this._initialQuery =
      this.route.paramMap
        .subscribe((params: ParamMap) => {
          this._docId = +params.get('id');
          this._docType = +params.get('type');
          console.log('*** EditDocumentInventoryComponent in param subscribe: (' + this._docType + ') (' + this._docId + ') ' + JSON.stringify(params));

          this.loadDocumentContent();
        });
  }

  /** Updates `DOCDATE` and auto-computes `DOCEXPDATE`/`DOCVALDATE` from `docTypeInfo` terms. */
  onDateSelect(event) {
    console.log('*** EditDocumentInventoryComponent in onDateSelect: ' + JSON.stringify(event));

    let dddd = new Date(event);
    this.document.DOCDATE = dddd;

    this.document.DOCVALDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTVALTERM || 0))
    this.document.DOCEXPDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTEXPTERM || 0))

    this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
    this.DOCVALDATE = new Date(this.document.DOCVALDATE);
  }

  /** Updates `DOCEXPDATE` when the expiry date picker fires. */
  onDateExpSelect(event) {
    console.log('*** EditDocumentInventoryComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCEXPDATE = event;
  }

  /** Updates `DOCVALDATE` when the validity date picker fires. */
  onDateValSelect(event) {
    console.log('*** EditDocumentInventoryComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCVALDATE = event;
  }

  /** Applies a raw currency-ID change emitted by the currency dropdown. */
  onDocCurrencyChange(event) {
    console.log('*** EditDocumentInventoryComponent in onDocCurrencyChange: ' + JSON.stringify(event));

    this.document.DOCCURRID = event;
  }

  /** Applies both the currency ID and the exchange rate from the currency-selector component. */
  onCurrencySelection(event) {
    if (this.document.DOCCURRID != event.CURID) {
      this.document.DOCCURRID = event.CURID;
    }
    this.document.DOCRATEVAL = event.CURRATE;
  }

  /** Handles selection of a recently-used document row (diagnostic log only). */
  onRecentRowSelect(selectedId) {
    console.log('*** EditDocumentInventoryComponent in onRecentRowSelect: ' + selectedId);
  }

  /** Logs the accordion tab index that was opened. */
  onTabOpen(event) { console.log('*** EditDocumentInventoryComponent in onTabOpen: ' + event.index); }

  /** Sets or clears `PRIMDOCID` from the parent-document picker. */
  parentDocumentSelected(event) {
    console.log('*** EditDocumentInventoryComponent in parentDocumentSelected: ' + JSON.stringify(event));
    this.document.PRIMDOCID = (event) ? event.DOCID : null;
  }

  /** Applies the selected security group ID to `SGMGROUPID`. */
  onSelectedSecurityGroup(sg: SecurityGroups) {
    console.log('*** EditDocumentInventoryComponent in onSelectedSecurityGroup: ' + JSON.stringify(sg));
    this.documentSecurityGroup = sg;
    this.document.SGMGROUPID = sg.SECGROUPID;
  }

  /**
   * Saves the document. PUT existing (`_docId > 0`) via a `concat` chain that re-fetches
   * `getDocumentNameById` to refresh the browser title after save. POST new documents
   * (`_docId == 0`) and route to the new ID.
   */
  onConfirm(e: number) {
    console.log('*** EditDocumentInventoryComponent onConfirm: ' + e)

    switch (e) {
      default:
        //console.log('*** EditDocumentInventoryComponent onConfirm: (Main data) ');
        //console.log('*** EditDocumentInventoryComponent buttonSaveAllClick: document ' + JSON.stringify(this.document));
        //console.log('*** EditDocumentInventoryComponent buttonSaveAllClick: documentCopy ' + JSON.stringify(this.documentCopy));

        if (this._docId) {
          this._documentSaveQuery =
            concat(
              this.docApi.putDocumentById(this.document)
                .pipe(map(data => {
                  this.documentCopy = { ... this.document };
                  this.document = { ... this.document };        // Trigger the changes in child components
                  console.log('*** EditDocumentInventoryComponent onConfirm: *** PUT *** ');
                })),
              this.docApi.getDocumentNameById(this._docId)
                .pipe(map((data) => {
                  let docName: any = data[0];

                  if (docName) {
                    this.document.DOCNAMESTR = docName.DOCNAMESTR;
                    this.titleService.setTitle(this.document.DOCNAMESTR);

                  }
                }))
            )
              .subscribe();
        } else {
          this._documentPostQuery =
            this.docApi.postDocumentById(this.document)
              .pipe(map((data) => {
                console.log('*** EditDocumentInventoryComponent onConfirm: *** on Post data map *** ' + JSON.stringify(data));
                this._docId = data['DOCID'];
              }))
              .subscribe(() => {
                this.router.navigate(['/edit-document', this._docId]);
                console.log('*** EditDocumentInventoryComponent onConfirm: *** POST *** ');
              });
        }
        break;
    }
  }

  /** Shows the conditions panel and hides all other section panels. */
  showConditions(event) {
    console.log('*** EditDocumentInventoryComponent in showConditions: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = true;
    this.showTabTable = false;
  }

  /** Shows the line-items table panel and hides all other section panels. */
  showContent(event) {
    console.log('*** EditDocumentInventoryComponent in showContent: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = false;
    this.showTabTable = true;
  }

  /** Handles a new-document creation event from the toolbar (currently a no-op stub). */
  onNewDocument(event) {
    console.log('*** EditDocumentInventoryComponent onNewDocument: ' + JSON.stringify(event))

    //    this.newDocumentOP.hide();
  }

  /** Re-spreads `document` to trigger child component refresh after a refill operation. */
  onRefillDocument(event) {
    console.log('*** EditDocumentInventoryComponent onRefillDocument: ' + JSON.stringify(event))
    if (event) {
      this.document = { ...this.document }; // Trigger reload
      //  environment.production || console.table(this.document);
      //  environment.production || console.table(this.document.items);
    }
  }

  /**
   * Prepares `documentSpecific` export labels and checks for highlighted rows via
   * `from(items).pipe(switchMap)`. Opens the export dialog directly when all rows are
   * clean, or prompts for confirmation when highlighted rows are present in production.
   */
  onExportDocument(event) {
    console.log('*** EditDocumentInventoryComponent onExportDocument: ' + JSON.stringify(event))
    //environment.production || console.table(this.document);
    environment.production || console.table(this.document.items);

    this.documentSpecific = {

      Owner: {
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Контактний телефон',
      },

      Header: {
        DateLabel: 'Дата',
        DocumentNameLabel: 'Назва',
        DocumentNumberLabel: 'Номер',
        ValidToLabel: 'Дійсний до',
        ExpiredOnLabel: 'Дата виконання',

        CurrencyNameLabel: 'Валюта документа',
        CurrencyRateLabel: 'Курс валюти',
        MainCurrencyNameLabel: 'Валюта документа',

      },

      Seller: {
        CompanyNameLabel: 'ПРОДАВЕЦЬ',
        CompanyIdLabel: 'Код ЄДРПОУ',
        VATIDLabel: 'Код платника ПДВ',
        VATIDDocLabel: 'Свідоцтво ПДВ',
        AccountLabel: 'Рахунок',
        BankLabel: 'Банк',
        AddressLabel: 'Адреса',
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Телефон',
      },

      Buyer: {
        CompanyNameLabel: 'ПОКУПЕЦЬ',
        CompanyIdLabel: 'Код ЄДРПОУ',
        VATIDLabel: 'Код платника ПДВ',
        VATIDDocLabel: 'Свідоцтво ПДВ',
        AccountLabel: 'Рахунок',
        BankLabel: 'Банк',
        AddressLabel: 'Адреса',
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Телефон',
      },

      ItemsLabel: {
        ItemCodeLabel: 'Код товару',
        ItemNameLabel: 'Найменування товару',
        ItemTariffCodeLabel: 'Код УКТЗЕД',
        ItemNoteLabel: 'Примітки',
        ItemPriceLabel: 'Ціна з ПДВ',
        ItemVPriceLabel: 'Ціна в валюті',
        ItemSumWoVATLabel: 'Сума без ПДВ',
        ItemPriceWoVATLabel: 'Ціна без ПДВ',
        ItemRetailPriceLabel: 'Роздріб',
        ItemRetailVPriceLabel: 'Роздріб в валюті',
        ItemDiscountLabel: 'Знижка',
        ItemQntyLabel: 'Кількість',
        ItemSumLabel: 'Сума з ПДВ',
        ItemVSumLabel: 'Сума в валюті',
        ItemVATLabel: 'ПДВ',

        ItemVendorPriceLabel: 'ItemVendorPriceLabel',
        ItemInvoicePriceLabel: 'ItemInvoicePriceLabel',
        ItemInvoiceSumLabel: 'ItemInvoiceSumLabel',

        ItemIncomePriceLabel: 'ItemIncomePriceLabel',
        ItemIncomeSumLabel: 'ItemIncomeSumLabel',

        ItemProdWeightLabel: 'ItemProdWeightLabel',
        ItemProdWeightSumLabel: 'ItemProdWeightSumLabel',

      },

      Conditions: {
        DeliveryConditionsLabel: 'Умови постачання',
        DeliveryTermsLabel: 'Очікуванний термін виконаня (днів)',
        DocumentNoteLabel: 'Примітки документа',
      },

      Summary: {
        TotalSumLabel: 'Загалом, з ПДВ',
        TotalVSumLabel: 'Загалом, з ПДВ, в валюті',
        TotalSumWithOutVATLabel: 'Сума без ПДВ',
        TotalSumVATLabel: 'Сума ПДВ',
        TotalItemsLabel: 'Усього найменувань',
        TotalQuantityLabel: 'Загальна кількість',
        TotalByWordsLabel: 'Усього прописом',
      }
    };

    let itsOk = true;

    from(this.document.items)
      .pipe(switchMap(di => of(di)))
      .subscribe(e => {

        itsOk = itsOk && !e.hlt;
      });

    if (!environment.production || itsOk) {
      this.exportDS = true;
    } else {
      this.confirmExportDocumentText = this.document.DOCNUMBER;
      this.exportConfirmDlg = true;
    }
  }

  /** Proceeds with export after the user confirms the highlighted-rows warning dialog. */
  onExportDocumentConfirmation(event) {
    console.log('*** EditDocumentInventoryComponent onExportDocumentConfirmation: ' /*+ JSON.stringify(event)*/);

    this.exportDS = true;
  }

  /** Deletes the document and navigates back to `/documents` after confirmation. */
  onDeleteDocumentConfirmation(event) {
    console.log('*** EditDocumentInventoryComponent onDeleteDocumentConfirmation: '/* + JSON.stringify(event)*/);

    this._deleteDocQuery =
      this.docApi.deleteDocumentById(this.document.DOCID).subscribe(() => {
        console.log('*** EditDocumentInventoryComponent onDeleteDocumentConfirmation: DONE!');
        this.router.navigate(['/documents']);
      });
  }

  /** Checks for child documents before enabling the delete confirmation dialog. */
  deleteDocumentClick(event) {
    console.log('*** EditDocumentInventoryComponent deleteDocumentClick: ' /*+ JSON.stringify(event)*/);

    this._childDocQuery =
      this.docApi.getChildDocList(this._docId).pipe(map(doc => {
        if (doc.length == 0) {
          this.confirmDeleteDocumentText = 'DELETE ' + this.document.DOCNUMBER;
          this.confirmDeleteDocumentOn = true;
        } else {
          this.messageService.add({ severity: 'error', summary: 'Этот документ имеет подчиненные - удалить невозможно.' });
        }
      })).subscribe();
  }

  /** Copies `DOCMANDEPID` → `DOCCLIDEPID` and `DOCMANPERID` → `DOCPERSONID` from the event. */
  onDocumentCompanyChanges(event) {
    console.log('*** EditDocumentInventoryComponent onDocumentCompanyChanges: ' /*+ JSON.stringify(event)*/);

    //this.document.DOCMANCLIID = event.DOCMANCLIID;
    //this.document.DOCMANDEPID = event.DOCMANDEPID;
    //this.document.DOCMANPERID = event.DOCMANPERID;
    this.document.DOCCLIDEPID = event.DOCMANDEPID;
    this.document.DOCPERSONID = event.DOCMANPERID;
  }

  /** Handles client-panel changes (currently a no-op stub). */
  onDocumentClientChanges(event) {
    console.log('*** EditDocumentInventoryComponent onDocumentClientChanges: '/* + JSON.stringify(event)*/);

    //this.document.DOCCLIENTSID = event.DOCCLIENTSID;
  }

  /** Replaces `document.items` with the imported rows and signals the table via `knockKnock`. */
  onImportDone(event) {
    console.log('*** EditDocumentInventoryComponent onImportDone: ');

    //    environment.production || console.table(event);

    this.document.items = event;

    this.knockKnock = true;
  }
}

